import React from 'react';

import { Divider } from '@mui/material';

import Row from 'components/Row';
import CustomFieldDisplayValue from 'components/SharedFormComponents/CustomFieldDisplayValue';
import { EntityModules, ProtectorType } from 'core/constants';
import { RepeatingProperties } from 'containers/EntitiesFormSubmission/components/RepeatingGroupField';
import { checkIfAllAnswersInSectionAreEmpty, getContentForKey } from 'stores/EventDetails/helpers';
import { AnswerTypes } from 'utils/api/modules/incidents/incidents.types';
import { ImageType } from 'utils/api/modules/entities/entities.types';
import EventDetailsStore from 'stores/EventDetails';

import { tidyLabel } from 'components/SharedFormComponents/PartnerAnswer';

import { AnswerValue } from './SubmissionHistory';
import { SectionHeader, RowContainer } from './styles';

type SubmissionForm = NonNullable<EventDetailsStore['details']>['submission']['form']['form'];

const SubmissionSection = ({
  form,
  sectionKey,
  content,
  absent,
  hideEmptyAnswers,
  imported,
}: {
  form: SubmissionForm;
  sectionKey: string;
  content: Record<string, AnswerTypes>;
  absent?: Set<string>;
  hideEmptyAnswers?: boolean;
  imported?: boolean;
}): React.ReactElement | null => {
  const { title, order, properties } = form.properties[sectionKey];

  if (hideEmptyAnswers && checkIfAllAnswersInSectionAreEmpty(form, sectionKey, content)) {
    return null;
  }

  return (
    <>
      <SectionHeader>
        <p>{title}</p>
      </SectionHeader>
      {order.map((property, index) => {
        const {
          description,
          questionType,
          properties: { lookupType, image },
        } = properties[property];

        const value = getContentForKey({ form, content, key: property, sectionKey });
        const fieldLabel = (imported && description ? tidyLabel(description) : description) || '-';

        const isAbsent = absent?.has(property) ?? false;
        if (hideEmptyAnswers && value === undefined && !isAbsent) {
          return null;
        }

        return (
          <React.Fragment key={property}>
            <RowContainer>
              <Row name={fieldLabel} valueTestId={`submission-answer-${property}`}>
                <AnswerValue absent={isAbsent} questionKey={property}>
                  <CustomFieldDisplayValue
                    schema={undefined}
                    type={questionType as ProtectorType}
                    value={value as AnswerTypes}
                    lookupType={lookupType as unknown as EntityModules}
                    label={fieldLabel}
                    isImported={imported}
                    submissionTab
                    repeatingGroup={
                      (properties[property].properties as unknown as { repeatingGroup?: RepeatingProperties })
                        .repeatingGroup
                    }
                  />
                </AnswerValue>
              </Row>
            </RowContainer>
            {image && (
              <img
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                src={(image as unknown as ImageType).file as string}
                alt="question"
              />
            )}
            {index !== order.length - 1 && <Divider />}
          </React.Fragment>
        );
      })}
    </>
  );
};

export default SubmissionSection;
